import { useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import { colors } from '../../tokens/colors';
import { borderRadius } from '../../tokens/spacing';

/**
 * Resumo de erros no topo do formulário.
 *
 * O DESIGN.md prescreve "Error Summary no topo" para formulários complexos, e a
 * varredura encontrou **zero** ocorrência no frontend. Em formulário longo, erro
 * apenas inline obriga o usuário a caçar o campo — e quem usa leitor de tela
 * sequer sabe quantos erros existem.
 *
 * Recebe foco ao aparecer e cada item leva ao campo correspondente por `id`.
 */

export interface FieldError {
  /** `id` do campo com erro, usado para focar e rolar até ele. */
  readonly fieldId: string;
  readonly label: string;
  readonly message: string;
}

export interface ErrorSummaryProps {
  readonly errors: readonly FieldError[];
  readonly title?: string;
  readonly className?: string;
}

export function ErrorSummary({
  errors,
  title,
  className,
}: ErrorSummaryProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  const count = errors.length;

  useEffect(() => {
    if (count > 0) ref.current?.focus();
  }, [count]);

  if (count === 0) return null;

  /*
    "campo com problema" só vale quando todo item aponta para um campo. Falha de
    transmissão à SEFAZ não é campo: com erro global na lista o título fala de
    "problema", não de "campo".
  */
  const allFieldScoped = errors.every((e) => e.fieldId);
  const noun = allFieldScoped
    ? (count === 1 ? 'campo com problema' : 'campos com problema')
    : (count === 1 ? 'problema' : 'problemas');
  const heading = title ?? `Há ${count} ${noun}`;

  const focusField = (fieldId: string): void => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus();
  };

  return (
    <div
      ref={ref}
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
      className={cn('ds-error-summary', className)}
      style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        border: `1px solid ${colors.error.DEFAULT}`,
        borderLeftWidth: '4px',
        borderRadius: borderRadius.lg,
        background: colors.error[50],
        outline: 'none',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: colors.error[700],
        }}
      >
        {heading}
      </p>
      <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.125rem' }}>
        {errors.map((err, index) => (
          /*
            `fieldId` vazio = erro do formulario como um todo (ex.: falha na
            transmissao a SEFAZ), sem campo para focar. Nesse caso o item sai
            como texto simples: item sublinhado e clicavel que nao leva a lugar
            nenhum e pior que texto. A chave inclui o indice porque varios erros
            globais compartilham o `fieldId` vazio.
          */
          <li
            key={`${err.fieldId}-${index}`}
            style={{ fontSize: '0.8125rem', color: colors.error[700] }}
          >
            {err.fieldId ? (
              <button
                type="button"
                onClick={() => focusField(err.fieldId)}
                style={{
                  border: 0,
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                  textDecoration: 'underline',
                  textAlign: 'left',
                }}
              >
                {`${err.label}: ${err.message}`}
              </button>
            ) : (
              <span>{err.message}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
